import React, { useState } from 'react';
import api from '../services/api';

const StatementsUploadAll = () => {
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSheetModal, setShowSheetModal] = useState(false);
    const [sheets, setSheets] = useState([]);
    const [selectedSheets, setSelectedSheets] = useState([]);
    const [filePath, setFilePath] = useState('');
    const [result, setResult] = useState(null);
    const [processingLoading, setProcessingLoading] = useState(false);
    const [sheetFilter, setSheetFilter] = useState('');

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setMessage('');
        setResult(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setMessage('Пожалуйста, выберите файл');
            return;
        }

        setLoading(true);
        setMessage('');
        setResult(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.getStatementsSheets(formData);
            
            if (response.data && response.data.sheets) {
                setSheets(response.data.sheets);
                setFilePath(response.data.filePath || file.name);
                setSelectedSheets([]);
                setSheetFilter('');
                setShowSheetModal(true);
            } else {
                setMessage('Ошибка получения списка листов');
            }
        } catch (error) {
            setMessage('Ошибка загрузки файла');
            console.error('Upload error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSheetToggle = (sheetName) => {
        setSelectedSheets(prev => {
            if (prev.includes(sheetName)) {
                return prev.filter(s => s !== sheetName);
            } else {
                return [...prev, sheetName];
            }
        });
    };

    const handleSelectAll = () => {
        const filteredSheetsSet = new Set(filteredSheets);
        const allFilteredSelected = filteredSheets.every(sheet => selectedSheets.includes(sheet));
        
        if (allFilteredSelected && filteredSheets.length > 0) {
            // Снять выделение только с отфильтрованных листов
            setSelectedSheets(prev => prev.filter(sheet => !filteredSheetsSet.has(sheet)));
        } else {
            // Добавить все отфильтрованные листы к выбранным
            setSelectedSheets(prev => {
                const newSelected = new Set(prev);
                filteredSheets.forEach(sheet => newSelected.add(sheet));
                return Array.from(newSelected);
            });
        }
    };

    const handleLoadFile = async () => {
        if (selectedSheets.length === 0) {
            setMessage('Пожалуйста, выберите хотя бы один лист');
            return;
        }

        setProcessingLoading(true);
        setMessage('');

        try {
            const response = await api.loadStatementsFile({
                filePath: filePath,
                sheets: selectedSheets
            });

            if (response.data) {
                setResult(response.data);
                setShowSheetModal(false);
                setMessage(response.data.message || 'Обработка завершена');
            } else {
                setMessage(response.message || 'Файл успешно обработан');
                setShowSheetModal(false);
            }
        } catch (error) {
            setMessage('Ошибка обработки файла');
            console.error('Processing error:', error);
        } finally {
            setProcessingLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowSheetModal(false);
        setSelectedSheets([]);
        setSheetFilter('');
    };

    const filteredSheets = sheets.filter(sheet =>
        sheet.toLowerCase().includes(sheetFilter.toLowerCase())
    );

    return (
        <div className="page">
            <h2>Загрузка всех выписок из Excel</h2>
            <p>Загрузите Excel файл с данными банковских выписок на нескольких листах</p>
            
            <div className="upload-container">
                <form onSubmit={handleSubmit} className="upload-form">
                    <div className="form-group">
                        <label htmlFor="file">Выберите Excel файл:</label>
                        <input
                            type="file"
                            id="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            required
                        />
                    </div>
                    
                    <button 
                        type="submit" 
                        disabled={loading || !file}
                        className="upload-btn"
                    >
                        {loading ? 'Загрузка...' : 'Загрузить'}
                    </button>
                </form>

                {message && (
                    <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                        {message}
                    </div>
                )}

                {result && (
                    <div className="upload-result">
                        <h4>Результаты обработки:</h4>
                        <div className="result-stats">
                            <div className="stat success">
                                <strong>Всего успешно:</strong> {result.total?.success || 0}
                            </div>
                            <div className="stat error">
                                <strong>Всего ошибок:</strong> {result.total?.errors || 0}
                            </div>
                        </div>

                        {result.results && result.results.length > 0 && (
                            <div className="sheets-results">
                                <h5>Результаты по листам:</h5>
                                {result.results.map((sheetResult, index) => (
                                    <div key={index} className="sheet-result">
                                        <h6>{sheetResult.sheetName}</h6>
                                        <div className="sheet-stats">
                                            <span className="success">Успешно: {sheetResult.success}</span>
                                            <span className="error">Ошибок: {sheetResult.errors}</span>
                                        </div>
                                        {sheetResult.warnings && sheetResult.warnings.length > 0 && (
                                            <div className="warnings-list">
                                                {sheetResult.warnings.map((warning, wIndex) => (
                                                    <div key={wIndex} className="warning-item">
                                                        {warning}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {result.total?.warnings && result.total.warnings.length > 0 && (
                            <div className="warnings-section">
                                <h5>Общие предупреждения:</h5>
                                <div className="warnings-list">
                                    {result.total.warnings.map((warning, index) => (
                                        <div key={index} className="warning-item">
                                            {warning}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="upload-info">
                    <h4>Формат файла для выписок:</h4>
                    <ul>
                        <li>Файл может содержать несколько листов</li>
                        <li>Данные начинаются с 2-й строки</li>
                        <li>Столбец A (1) - Дата выписки</li>
                        <li>Столбец B (2) - Номер</li>
                        <li>Столбец C (3) - Вид операции</li>
                        <li>Столбец D (4) - Контрагент</li>
                        <li>Столбец E (5) - ИНН контрагента</li>
                        <li>Столбец F (6) - БИК банка контрагента</li>
                        <li>Столбец G (7) - Счет контрагента</li>
                        <li>Столбец H (8) - Дебет, RUR</li>
                        <li>Столбец I (9) - Кредит, RUR</li>
                        <li>Столбец J (10) - Назначение</li>
                    </ul>
                    
                    <div className="upload-note">
                        <strong>Примечание:</strong> После загрузки файла вы сможете выбрать листы для обработки.
                        Система автоматически свяжет выписки с соответствующими приказами по номеру дела.
                    </div>
                </div>
            </div>

            {/* Modal for sheet selection */}
            {showSheetModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Выберите листы для обработки</h3>
                            <button className="modal-close" onClick={handleCloseModal}>×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="sheet-filter-container">
                                <input
                                    type="text"
                                    className="sheet-filter-input"
                                    placeholder="Фильтр по названию листа..."
                                    value={sheetFilter}
                                    onChange={(e) => setSheetFilter(e.target.value)}
                                />
                            </div>

                            <div className="select-all-container">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={filteredSheets.length > 0 && filteredSheets.every(sheet => selectedSheets.includes(sheet))}
                                        onChange={handleSelectAll}
                                    />
                                    <span>Выбрать все ({selectedSheets.length} / {sheets.length})</span>
                                </label>
                            </div>
                            
                            <div className="sheets-list">
                                {filteredSheets.length === 0 ? (
                                    <div className="no-sheets-found">Листы не найдены</div>
                                ) : (
                                    filteredSheets.map((sheet, index) => (
                                        <div key={index} className="sheet-item">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSheets.includes(sheet)}
                                                    onChange={() => handleSheetToggle(sheet)}
                                                />
                                                <span>{sheet}</span>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCloseModal}
                                disabled={processingLoading}
                            >
                                Отмена
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={handleLoadFile}
                                disabled={processingLoading || selectedSheets.length === 0}
                            >
                                {processingLoading ? 'Обработка...' : 'Загрузить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }

                .modal-content {
                    background: white;
                    border-radius: 8px;
                    width: 90%;
                    max-width: 500px;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .modal-header {
                    padding: 20px;
                    border-bottom: 1px solid #e0e0e0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 1.25rem;
                }

                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #666;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    color: #333;
                }

                .modal-body {
                    padding: 20px;
                    overflow-y: auto;
                    flex: 1;
                }

                .sheet-filter-container {
                    margin-bottom: 15px;
                }

                .sheet-filter-input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    font-size: 0.95rem;
                    box-sizing: border-box;
                    outline: none;
                    transition: border-color 0.2s;
                }

                .sheet-filter-input:focus {
                    border-color: #007bff;
                    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
                }

                .select-all-container {
                    margin-bottom: 15px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .select-all-container label {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    font-weight: 600;
                }

                .sheets-list {
                    max-height: 400px;
                    overflow-y: auto;
                }

                .sheet-item {
                    padding: 10px;
                    border-bottom: 1px solid #f0f0f0;
                }

                .sheet-item:last-child {
                    border-bottom: none;
                }

                .sheet-item label {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                }

                .sheet-item input[type="checkbox"],
                .select-all-container input[type="checkbox"] {
                    margin-right: 10px;
                    cursor: pointer;
                    width: 18px;
                    height: 18px;
                }

                .no-sheets-found {
                    padding: 20px 0;
                    text-align: center;
                    color: #888;
                    font-style: italic;
                }

                .modal-footer {
                    padding: 20px;
                    border-top: 1px solid #e0e0e0;
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }

                .sheets-results {
                    margin-top: 20px;
                }

                .sheet-result {
                    background: #f9f9f9;
                    padding: 15px;
                    margin-bottom: 15px;
                    border-radius: 4px;
                    border-left: 4px solid #007bff;
                }

                .sheet-result h6 {
                    margin: 0 0 10px 0;
                    font-size: 1.1rem;
                    color: #333;
                }

                .sheet-stats {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 10px;
                }

                .sheet-stats .success {
                    color: #28a745;
                }

                .sheet-stats .error {
                    color: #dc3545;
                }
            `}</style>
        </div>
    );
};

export default StatementsUploadAll;
